/**
 * ProjectDropdown - AI Foundry project & resource selector
 *
 * Fetches and displays AI Foundry projects (v2) and Azure OpenAI accounts (v1).
 * Projects are grouped by parent account; OpenAI accounts appear as standalone entries.
 * Used for FoundryAgentService connection type.
 */

import { useMemo, useCallback } from 'react';
import {
  makeStyles,
  tokens,
  shorthands,
  Field,
  Button,
  Spinner,
  Text,
} from '@coreai/fluentui-react';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import { SearchableCombobox, type SearchableComboboxOption } from '@/components/shared';
import { useCognitiveProjects, useOpenAIAccounts } from '@/hooks/useAzureResources';
import { buildProjectEndpoint } from '@/services/azureResourceService';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  fieldRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: tokens.spacingHorizontalS,
  },
  combobox: {
    flex: 1,
    minWidth: 0,
  },
  refreshButton: {
    flexShrink: 0,
  },
  optionSubtext: {
    color: tokens.colorNeutralForeground3,
  },
  projectOption: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: tokens.spacingHorizontalS,
  },
  projectInfo: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  projectBadge: {
    fontSize: tokens.fontSizeBase100,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '1',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
});

/** Unified item type for both projects and standalone OpenAI accounts */
interface DropdownItem {
  id: string;
  displayName: string;
  resourceGroup: string;
  badge: 'Project (Default)' | 'Azure OpenAI';
  /** For projects: account name; for accounts: own name */
  accountName: string;
  /** For projects: project name used in endpoint; for accounts: account name */
  projectName: string;
  /** True for standalone OpenAI accounts (v1) */
  isOpenAIAccount: boolean;
}

export interface ProjectDropdownProps {
  /** Subscription ID to filter projects */
  subscriptionId: string;
  /** Currently selected project ID */
  value: string;
  /** Callback when selection changes */
  onChange: (projectId: string) => void;
  /** Callback when endpoint is derived */
  onEndpointDerived?: (endpoint: string) => void;
  /** Field label */
  label?: string;
  /** Whether the field is required */
  required?: boolean;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Validation error message */
  error?: string;
}

export const ProjectDropdown = ({
  subscriptionId,
  value,
  onChange,
  onEndpointDerived,
  label = 'AI Foundry Project',
  required = false,
  disabled = false,
  error,
}: ProjectDropdownProps) => {
  const styles = useStyles();

  const {
    data: projects,
    isLoading: projectsLoading,
    error: projectsError,
    refetch: refetchProjects,
  } = useCognitiveProjects(subscriptionId, !!subscriptionId);

  const {
    data: openAIAccounts,
    isLoading: accountsLoading,
    refetch: refetchAccounts,
  } = useOpenAIAccounts(subscriptionId, !!subscriptionId);

  const isLoading = projectsLoading || accountsLoading;
  const fetchError = projectsError;

  // Build unified dropdown items grouped by account/resource name
  const groupedItems = useMemo(() => {
    const groups: Record<string, DropdownItem[]> = {};

    // Add v2 projects grouped by parent account
    (projects ?? []).forEach((project) => {
      const accountName = project.accountName;
      const projectName = project.name.split('/').pop() ?? project.name;

      if (!groups[accountName]) {
        groups[accountName] = [];
      }

      groups[accountName].push({
        id: project.id,
        displayName: projectName,
        resourceGroup: project.resourceGroup,
        badge: 'Project (Default)',
        accountName,
        projectName,
        isOpenAIAccount: false,
      });
    });

    // Add standalone OpenAI accounts as their own group
    (openAIAccounts ?? []).forEach((account) => {
      const name = account.name;
      // Skip if this account already appears as a parent of projects
      if (groups[name]) return;

      groups[name] = [{
        id: account.id,
        displayName: name,
        resourceGroup: account.resourceGroup,
        badge: 'Azure OpenAI',
        accountName: name,
        projectName: name,
        isOpenAIAccount: true,
      }];
    });

    return groups;
  }, [projects, openAIAccounts]);

  // Flat lookup for selected text + handler resolution.
  const allItems = useMemo(() => {
    const items: DropdownItem[] = [];
    for (const group of Object.values(groupedItems)) {
      items.push(...group);
    }
    return items;
  }, [groupedItems]);

  // O(1) lookup by id for renderOption / handleSelect. Avoids a linear scan
  // per visible option each render.
  const itemsById = useMemo(() => {
    const map = new Map<string, DropdownItem>();
    for (const item of allItems) map.set(item.id, item);
    return map;
  }, [allItems]);

  // Build flat options for SearchableCombobox. `groupLabel` carries the
  // account/resource name so options under the same account render together
  // and the user can type the account name to filter.
  const options = useMemo<SearchableComboboxOption[]>(() => {
    const opts: SearchableComboboxOption[] = [];
    for (const [groupName, items] of Object.entries(groupedItems)) {
      for (const item of items) {
        opts.push({
          value: item.id,
          text: item.displayName,
          secondaryText: `/${item.resourceGroup}`,
          groupLabel: groupName,
          searchTerms: [item.resourceGroup, item.badge],
        });
      }
    }
    return opts;
  }, [groupedItems]);

  const renderOption = useCallback(
    (opt: SearchableComboboxOption) => {
      const item = itemsById.get(opt.value);
      const badge = item?.badge;
      return (
        <div className={styles.projectOption}>
          <div className={styles.projectInfo}>
            <Text weight="semibold">{opt.text}</Text>
            {opt.secondaryText && (
              <Text size={200} className={styles.optionSubtext}>
                {opt.secondaryText}
              </Text>
            )}
          </div>
          {badge && <span className={styles.projectBadge}>{badge}</span>}
        </div>
      );
    },
    [itemsById, styles],
  );

  const handleSelect = useCallback(
    (selectedValue: string) => {
      onChange(selectedValue);

      if (onEndpointDerived) {
        const item = itemsById.get(selectedValue);
        if (item) {
          if (item.isOpenAIAccount) {
            // OpenAI accounts: use account-level endpoint (no project path)
            const endpoint = `https://${item.accountName}.openai.azure.com`;
            onEndpointDerived(endpoint);
          } else {
            const endpoint = buildProjectEndpoint(item.accountName, item.projectName);
            onEndpointDerived(endpoint);
          }
        }
      }
    },
    [onChange, onEndpointDerived, itemsById]
  );

  const handleRefresh = useCallback(() => {
    refetchProjects();
    refetchAccounts();
  }, [refetchProjects, refetchAccounts]);

  const isDisabled = disabled || !subscriptionId;
  const validationState = error || fetchError ? 'error' : 'none';
  const validationMessage = error ?? fetchError?.message ?? null;
  const hasItems = options.length > 0;

  return (
    <div className={styles.container}>
      <Field
        label={label}
        required={required}
        validationState={validationState}
        validationMessage={validationMessage}
      >
        <div className={styles.fieldRow}>
          <SearchableCombobox
            className={styles.combobox}
            options={options}
            value={value}
            onChange={handleSelect}
            renderOption={renderOption}
            disabled={isDisabled || isLoading}
            placeholder={
              !subscriptionId
                ? 'Select a subscription first'
                : isLoading
                  ? 'Loading resources...'
                  : !hasItems
                    ? 'No projects found'
                    : 'Select a project or resource'
            }
            aria-label={label}
          />
          <Button
            className={styles.refreshButton}
            appearance="subtle"
            icon={isLoading ? <Spinner size="tiny" /> : <ArrowSyncRegular />}
            onClick={handleRefresh}
            disabled={isLoading || isDisabled}
            aria-label="Refresh projects"
            title="Refresh projects"
          />
        </div>
      </Field>
    </div>
  );
};

ProjectDropdown.displayName = 'ProjectDropdown';
